import loadable from "@loadable/component"
import PropTypes from "prop-types"
import { Navigate, Route } from "react-router-dom"

import CollectionFullLoading from "areas/collection/loaders"
import Footer from "areas/footer/jewlr"
import Header from "areas/header/jewlr"
import JsCookiesWarning from "areas/header/js-cookies-warning"
import FakeBeacon from "areas/helpdesk/fakeBeacon"
import CLSMonitor from "components/cls-monitor"
import MerchantLoading from "components/merchant-loading"
import MetaTags from "components/meta-tags"
import SkipToMainContent from "components/skip-to-main-content"
import HelpDeskProvider from "contexts/helpdesk"
import { jewlr_t, IS_CA, IS_INT, retry } from "helpers/application"
import { Routes } from "helpers/sentry"
import useCleanPath from "helpers/use-clean-path"
import usePrefetchPages from "helpers/use-prefetch-pages"
import fetchUser from "hoc/account/fetch-user"

const loadableDefaultOptions = {
  fallback: <MerchantLoading />,
}

const InfoRouteWrapper = loadable(
  () => retry(() => import("areas/info/jewlr/route/info-route-wrapper")),
  loadableDefaultOptions
)
const ArticleLanding = loadable(
  () => retry(() => import("areas/article/landing")),
  loadableDefaultOptions
)
const Cart = loadable(
  () => retry(() => import("areas/cart")),
  loadableDefaultOptions
)
const LoadCollection = loadable(
  () => retry(() => import("areas/collection/load-collection")),
  { fallback: <CollectionFullLoading /> }
)
const Helpdesk = loadable(
  () => retry(() => import("areas/helpdesk/helpdesk")),
  loadableDefaultOptions
)
const LandingWatches = loadable(
  () => retry(() => import("areas/info/jewlr/watches/landing")),
  loadableDefaultOptions
)
const DiamondStudEarrings = loadable(
  () => retry(() => import("areas/landing/diamond-stud-earrings/index")),
  loadableDefaultOptions
)
const Product = loadable(
  () => retry(() => import("areas/product/jewlr")),
  loadableDefaultOptions
)

const CollectionsForHer = loadable(
  () => retry(() => import("jewlr/pages/collections-for-her")),
  loadableDefaultOptions
)
const CollectionsForHim = loadable(
  () => retry(() => import("jewlr/pages/collections-for-him")),
  loadableDefaultOptions
)
const GetInspired = loadable(
  () => retry(() => import("jewlr/pages/get-inspired")),
  loadableDefaultOptions
)
const Account = loadable(
  () => retry(() => import("areas/account")),
  loadableDefaultOptions
)
const StandaloneReferral = loadable(
  () => retry(() => import("areas/account/referrals/standalone")),
  loadableDefaultOptions
)
const PasswordReset = loadable(
  () => retry(() => import("areas/password-reset")),
  loadableDefaultOptions
)
const Checkout = loadable(
  () => retry(() => import("areas/checkout")),
  loadableDefaultOptions
)
const Complete = loadable(
  () => retry(() => import("areas/checkout/order-complete/jewlr")),
  loadableDefaultOptions
)
const Contact = loadable(
  () => retry(() => import("areas/contact-form")),
  loadableDefaultOptions
)
const Order = loadable(
  () => retry(() => import("areas/order")),
  loadableDefaultOptions
)
const Payment = loadable(
  () => retry(() => import("areas/payments")),
  loadableDefaultOptions
)
const Info = loadable(
  () => retry(() => import("areas/info/jewlr")),
  loadableDefaultOptions
)
const AccountRegister = loadable(
  () => retry(() => import("areas/authenticate/register")),
  loadableDefaultOptions
)
const EmailPreferences = loadable(
  () => retry(() => import("areas/account/email-preferences")),
  loadableDefaultOptions
)
const ResubscribedComplete = loadable(
  () =>
    retry(
      () => import("areas/account/email-preferences/resubscribed-complete")
    ),
  loadableDefaultOptions
)
const UnsubscribedComplete = loadable(
  () =>
    retry(
      () => import("areas/account/email-preferences/unsubscribed-complete")
    ),
  loadableDefaultOptions
)
const Login = loadable(
  () => retry(() => import("areas/authenticate/login")),
  loadableDefaultOptions
)
const EmailSignUp = loadable(
  () => retry(() => import("areas/email-signup")),
  loadableDefaultOptions
)
const Tagger = loadable(
  () => retry(() => import("areas/tagger")),
  loadableDefaultOptions
)
const AboutUs = loadable(
  () => retry(() => import("areas/about-us/jewlr")),
  loadableDefaultOptions
)
const Covid = loadable(
  () => retry(() => import("areas/info/jewlr/covid")),
  loadableDefaultOptions
)
const InstantGiftDemo = loadable(
  () => retry(() => import("areas/instant-gift/explained/demo")),
  loadableDefaultOptions
)
const InstantGift = loadable(
  () => retry(() => import("areas/instant-gift/item")),
  loadableDefaultOptions
)
const InstantGiftPreview = loadable(
  () => retry(() => import("areas/instant-gift/item/preview-gift")),
  loadableDefaultOptions
)
const Reviews = loadable(
  () => retry(() => import("areas/info/reviews")),
  loadableDefaultOptions
)
const NWVibe = loadable(
  () => retry(() => import("jewlr/main/nw-vibe")),
  loadableDefaultOptions
)
const Vibe = loadable(
  () => retry(() => import("jewlr/main/vibe")),
  loadableDefaultOptions
)
const AttentiveSignup = loadable(
  () => retry(() => import("jewlr/pages/attentive-signup")),
  loadableDefaultOptions
)
const GenericNotFound = loadable(
  () => retry(() => import("app/generic-not-found")),
  loadableDefaultOptions
)
const GenericProductImport = loadable(
  () => retry(() => import("app/generic-product-import")),
  loadableDefaultOptions
)
const VipRewards = loadable(
  () => retry(() => import("areas/vip-rewards/jewlr")),
  loadableDefaultOptions
)
const Wishlist = loadable(
  () => retry(() => import("areas/wishlist")),
  loadableDefaultOptions
)
const GiftFinder = loadable(
  () => retry(() => import("areas/gift-finder")),
  loadableDefaultOptions
)
const GiftFinderQuiz = loadable(
  () => retry(() => import("areas/gift-finder/quiz")),
  loadableDefaultOptions
)

/**
 * The main App wrapper. This contains the top-level routing and passes through `data` when appropriate to children
 * components
 * @param  {object} options.match    For React Router
 * @param  {object} options.history  For React Router
 */

const InternalApp = ({
  article_data: articleData,
  checkout,
  collection_data,
  complete,
  gift_data: giftData,
  handleSkipToMainContent,
  helpdesk,
  helpdesk_article: helpdeskArticle,
  helpdesk_collection: helpdeskCollection,
  helpdesk_homepage: helpdeskHomepage,
  helpdesk_related_articles: helpdeskRelatedArticles,
  landing_data,
  menu,
  model,
  navTopRef,
  order_update_data: orderUpdateData,
  product_import: productImportData,
  sale,
  skippedRef,
  staticContext,
  tagger: taggerData,
  user_agent: userAgent,
}) => {
  const secondMicroBanner = sale.second_microbanner

  // const holdbackExp = useSelector((state) => state.experiment.holdback)

  // useEffect(() => {
  //   if (holdbackExp) {
  //     dispatch(setExperiment({ holdback: holdbackExp }))
  //   }
  // }, [dispatch, holdbackExp])

  usePrefetchPages()
  useCleanPath()

  return (
    <div
      style={{
        "--total-header-height": secondMicroBanner
          ? "calc(var(--countdown-banner-height) + var(--header-menu-height) + var(--second-microbanner-height))"
          : "calc(var(--countdown-banner-height) + var(--header-menu-height)",
      }}
    >
      <MetaTags />
      <span ref={navTopRef} tabIndex="-1" />
      <SkipToMainContent handleSkipToMainContent={handleSkipToMainContent} />
      <Header menu={menu} />
      <main>
        <span ref={skippedRef} tabIndex="-1" />
        <JsCookiesWarning />
        <Routes>
          <Route element={<AboutUs />} path="/about-us" />
          {sl.config.enable_rewards && (
            <Route element={<VipRewards />} path="/vip-rewards" />
          )}
          {sl.config.env !== "production" && (
            <Route
              element={
                <iframe
                  height="1500px"
                  src={"https://ask.jewlr.com"}
                  width="100%"
                />
              }
              path="/ask"
            />
          )}
          <Route
            element={<UnsubscribedComplete />}
            path="/email-preferences/unsubscribe-complete"
          />
          <Route
            element={<ResubscribedComplete />}
            path="/email-preferences/subscribe-complete"
          />
          <Route
            element={<EmailPreferences standalone />}
            path="/email-preferences/:guidToken"
          />
          <Route
            element={<EmailPreferences standalone />}
            path="/email-unsubscribe/:guidToken"
          />
          <Route element={<Account />} path="/account/*" />
          {sl.config.enable_rewards && (
            <Route
              element={<StandaloneReferral />}
              path="/referral/:guidToken"
            />
          )}
          <Route
            element={
              <InfoRouteWrapper
                description={`Reset your Jewlr account password securely. Use this page to recover access, update login details, and keep your ${jewlr_t("terms.personalized")} ${jewlr_t("terms.jewelry")} account safe.`}
                title="Jewlr Password Reset | Recover or Update Your Account Login"
              >
                <PasswordReset />
              </InfoRouteWrapper>
            }
            path="/password-reset"
          />
          <Route element={<Checkout checkout={checkout} />} path="/checkout" />
          <Route
            element={<Complete complete={complete} />}
            path="/checkout/complete"
          />
          <Route
            element={
              <InfoRouteWrapper newLayout title="Contact Us">
                <Contact />
              </InfoRouteWrapper>
            }
            path="/contact_form"
          />
          <Route element={<Login />} path="/signin" />
          <Route element={<AccountRegister />} path="/signup" />
          <Route element={<AccountRegister />} path="/register" />
          <Route element={<NWVibe />} path="/nwvibe" />
          <Route element={<Vibe />} path="/vibe" />
          {sl.config.enable_gift_preview_video ? (
            <Route element={<InstantGiftDemo />} path="/gift/demo" />
          ) : (
            <Route element={<Navigate replace to="/" />} path="/gift/demo" />
          )}
          <Route
            element={<InstantGiftPreview giftData={giftData} skipVideo />}
            path="/gift/share/:lineItemCode"
          />
          <Route
            element={<InstantGift giftData={giftData} />}
            path="/gift/:lineItemCode"
          />
          <Route
            element={<Order orderUpdateData={orderUpdateData} />}
            path="/order/*"
          />
          <Route
            element={
              <InfoRouteWrapper
                description={`Read verified Jewlr reviews from real customers. Discover ratings and testimonials on ${jewlr_t("terms.personalized")} rings, necklaces, and gifts handcrafted just for you.`}
                heading="Jewlr Reviews – What Our Customers Are Saying"
                newLayout
                showCta
                title="Jewlr Reviews - Real Customer Ratings & Testimonials"
              >
                <Reviews />
              </InfoRouteWrapper>
            }
            path="/reviews"
          />
          {sl.config.enable_watches && (
            <Route element={<LandingWatches />} path="/about-jewlr-watches" />
          )}
          <Route
            element={<DiamondStudEarrings />}
            path="/diamond-stud-earrings-landing"
          />
          {!IS_CA() && (
            <Route
              element={
                <InfoRouteWrapper newLayout title="COVID-19">
                  <Covid />
                </InfoRouteWrapper>
              }
              path="/covid19"
            />
          )}
          <Route element={<Info />} path="/info/*" />
          <Route element={<Cart />} path="/cart" />
          {sl.config.env !== "production" && (
            <Route
              element={<Tagger taggerData={taggerData} />}
              path="/products/tagger"
            />
          )}
          <Route
            element={<Product share staticContext={staticContext} />}
            path="/products/share/:styleCode/*"
          />
          <Route
            element={<Product staticContext={staticContext} />}
            path="/products/:styleCode/*"
          />
          <Route element={<Payment />} path="/payments/:id" />
          <Route
            element={
              <HelpDeskProvider>
                <Helpdesk
                  helpdesk={helpdesk}
                  helpdeskArticle={helpdeskArticle}
                  helpdeskCollection={helpdeskCollection}
                  helpdeskHomepage={helpdeskHomepage}
                  helpdeskRelatedArticles={helpdeskRelatedArticles}
                  staticContext={staticContext}
                />
              </HelpDeskProvider>
            }
            path="/helpdesk/*"
          />
          <Route
            element={<ArticleLanding articleData={articleData} />}
            path="/blog/*"
          />
          {(sl.config.site_country === "us" ||
            sl.config.site_country === "ca") && (
            <Route element={<AttentiveSignup />} path="/attn-signup" />
          )}

          <Route element={<CollectionsForHim />} path="/collections-for-him" />
          <Route
            element={<CollectionsForHer userAgent={userAgent} />}
            path="/collections-for-her"
          />
          <Route element={<GetInspired />} path="/get-inspired" />
          <Route element={<EmailSignUp />} path="/email-signup" />
          <Route element={<Wishlist />} path="/wishlist" />
          <Route element={<GiftFinderQuiz />} path="/gift-finder/quiz" />
          <Route element={<GiftFinder />} path="/gift-finder" />
          {!IS_INT() && (
            <Route
              element={<Navigate replace to="/gift-finder" />}
              path="/holiday-gift-finder/*"
            />
          )}
          <Route
            element={
              <LoadCollection
                collectionData={collection_data}
                landingData={landing_data}
                ssrModel={model}
                staticContext={staticContext}
              />
            }
            path="/:url_path?"
          />
          <Route
            element={
              <GenericProductImport productImportData={productImportData} />
            }
            path="/product_import/jobs/:id"
          />
          <Route
            element={<GenericNotFound staticContext={staticContext} />}
            path="*"
          />
        </Routes>
      </main>
      <Footer />
      <FakeBeacon />
      {sl.config.env !== "production" && <CLSMonitor />}
    </div>
  )
}

InternalApp.propTypes = {
  article_data: PropTypes.object,
  checkout: PropTypes.object,
  collection_data: PropTypes.object,
  complete: PropTypes.object,
  gift_data: PropTypes.object,
  handleSkipToMainContent: PropTypes.func,
  helpdesk: PropTypes.object,
  helpdesk_article: PropTypes.object,
  helpdesk_collection: PropTypes.object,
  helpdesk_homepage: PropTypes.object,
  helpdesk_related_articles: PropTypes.array,
  landing_data: PropTypes.object,
  menu: PropTypes.object,
  model: PropTypes.string,
  navTopRef: PropTypes.object,
  order_update_data: PropTypes.object,
  product_import: PropTypes.object,
  sale: PropTypes.object,
  skippedRef: PropTypes.object,
  staticContext: PropTypes.object,
  tagger: PropTypes.object,
  user_agent: PropTypes.string,
}

export default fetchUser(InternalApp)
